import { FC, memo, useCallback } from "react";
import { Paper, Stack, Typography } from "@mui/material";
import { AppSettingsTabProps, TVerificationAppElement, VerificationAppDocumentType } from "../../models";
import { AddStepButton } from "./AddStepButton";
import { ConditionsSection } from "./ConditionsSection";
import { ConstantsSection } from "./ConstantsSection";
import { moveElement } from "./helpers";
import { StepCard } from "./StepCard";

export const QuestionnaireTab: FC<AppSettingsTabProps> = memo(
  ({ config, onConfigChange }) => {
    const elements = config.elements ?? [];

    const handleStepChange = useCallback(
      (index: number, updated: TVerificationAppElement) => {
        onConfigChange((prev) => ({
          ...prev,
          elements: prev.elements?.map((el, i) => (i === index ? updated : el)),
        }));
      },
      [onConfigChange],
    );

    const handleStepDelete = useCallback(
      (index: number) => {
        onConfigChange((prev) => ({
          ...prev,
          elements: prev.elements?.filter((_, i) => i !== index),
        }));
      },
      [onConfigChange],
    );

    const handleStepMove = useCallback(
      (index: number, direction: "up" | "down") => {
        onConfigChange((prev) => {
          const list = prev.elements;
          if (!list?.length) return prev;
          const nextElements = moveElement(list, index, direction);
          return { ...prev, elements: nextElements };
        });
      },
      [onConfigChange],
    );

    const handleAddStep = useCallback(
      (type: VerificationAppDocumentType) => {
        onConfigChange((prev) => ({
          ...prev,
          elements: [
            ...(prev.elements ?? []),
            type === VerificationAppDocumentType.EntityRepresentatives
              ? {
                type,
                apps: {
                  representative: "",
                  ubo: "",
                },
              }
              : { type },
          ],
        }));
      },
      [onConfigChange],
    );

    return (
      <Stack spacing={2} sx={{ margin: "12px 0" }}>
        <ConstantsSection
          constants={config.constants}
          onConfigChange={onConfigChange}
        />
        <ConditionsSection
          conditions={config.conditions}
          onConfigChange={onConfigChange}
          validationConfig={config}
        />
        {elements.length === 0 ? (
          <Paper
            sx={{
              padding: "20px 32px",
              background: "#ffffff",
              borderRadius: "14px",
              border: "none",
            }}
          >
            <Typography color='text.secondary'>
              No steps configured. Add a step to get started.
            </Typography>
          </Paper>
        ) : (
          elements.map((step, i) => (
            <StepCard
              config={config}
              key={i}
              step={step}
              index={i}
              totalSteps={elements.length}
              onChange={(updated) => handleStepChange(i, updated)}
              onDelete={() => handleStepDelete(i)}
              onMove={(dir) => handleStepMove(i, dir)}
            />
          ))
        )}
        <AddStepButton onAdd={handleAddStep} />
      </Stack>
    );
  },
);
